import { useQuery, useQueryClient } from "@tanstack/react-query";
import apiFetch from "../../utils/apiFetch";
import { authHeaders } from "./helpers";
import type { PayRun, PayStub } from "./types";

export interface PayRunDetailData {
  run: PayRun;
  stubs: PayStub[];
}

export function usePayRunDetail(id: string | undefined) {
  return useQuery<PayRunDetailData>({
    queryKey: ["pay-run-detail", id],
    queryFn: async () => {
      if (!id) throw new Error("Missing pay run id");

      let runData: PayRun | undefined;
      const runRes = await apiFetch("/api/v1/payroll/runs/" + id, {
        headers: authHeaders(),
      });

      if (runRes.status === 404 || runRes.status === 405) {
        const listRes = await apiFetch("/api/v1/payroll/runs", {
          headers: authHeaders(),
        });
        if (!listRes.ok) {
          throw new Error("Could not load runs (HTTP " + listRes.status + ")");
        }
        const list = await listRes.json();
        runData = (Array.isArray(list) ? list : []).find(
          (r: PayRun) => String(r.id) === String(id)
        );
        if (!runData) throw new Error("Pay run not found");
      } else if (!runRes.ok) {
        const errBody = await runRes.json().catch(() => ({}));
        throw new Error(
          errBody.detail || "Could not load run (HTTP " + runRes.status + ")"
        );
      } else {
        runData = await runRes.json();
      }

      const stubsRes = await apiFetch("/api/v1/payroll/runs/" + id + "/stubs", {
        headers: authHeaders(),
      });
      let stubsList: PayStub[] = [];
      if (stubsRes.ok) {
        const stubsData = await stubsRes.json();
        stubsList = Array.isArray(stubsData)
          ? stubsData
          : stubsData.stubs || stubsData.data || [];
      }

      return { run: runData as PayRun, stubs: stubsList };
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  });
}

export function useInvalidatePayRunDetail(id: string | undefined) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ["pay-run-detail", id] });
}